import React from 'react';
import {useParams} from 'react-router-dom';
import {FeatureFlagEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkBulletins} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {BulletinDocumentTitle} from 'src/app/bulletins/bulletinHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {BulletinDashViewType} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import BulletinQrCodeTab from './components/BulletinQrCodeTab/BulletinQrCodeTab';
import GetActiveBulletin from './components/GetActiveBulletin/GetActiveBulletin';
import ListBulletins from './components/ListBulletins/ListBulletins';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const BulletinDashPage: React.FC = () => {
  useSetDocumentTitle(BulletinDocumentTitle);

  const perms = useModulePermissions(ModuleEnum.Bulletins);

  const navigate = useAppNavigate();
  const {view} = useParams();

  const {RenderedTabsComponent} = useUrlQueryTabV2<BulletinDashViewType>({
    view: (view ?? 'current') as BulletinDashViewType,
    defaultView: 'current',
    tabs: [
      {
        view: 'current',
        label: `Current Bulletin`,
        component: <GetActiveBulletin />,
      },
      {
        view: 'manage',
        label: `Manage Bulletins`,
        disabled: !perms.view,
        component: <ListBulletins />,
      },
      {
        view: 'qr-code',
        label: `QR Code`,
        disabled: !perms.view,
        component: <BulletinQrCodeTab />,
      },
    ],
    onSelectView: view => {
      navigate(PathUtility.BulletinDashboard(view as BulletinDashViewType));
    },
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Bulletins}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkBulletins.icon}>
          Bulletins
        </PageHeader.Title>
      </PageHeader>
      {RenderedTabsComponent}
    </ContentWrapper>
  );
};

// Register the routes with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.BulletinDashboard(':view'),
  element: <BulletinDashPage />,
  isPrivate: true,
});

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.BulletinDashboardParameterless(),
  element: <BulletinDashPage />,
  isPrivate: true,
});

export default BulletinDashPage;
